import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { 
  Plus, 
  Loader2, 
  Calendar as CalendarIcon,
  Package,
  User,
  Tags
} from 'lucide-react';

/**
 * Dialog for creating a new planned order in Order Pipeline
 */
export default function CreateOrderDialog({
  open,
  onOpenChange,
  formData,
  setFormData,
  selectedDate,
  handleDateSelect,
  customers,
  products,
  cobrandings,
  // Inline creation state
  showCreateCustomer,
  setShowCreateCustomer,
  showCreateProduct,
  setShowCreateProduct,
  showCreateCoBranding,
  setShowCreateCoBranding,
  newCustomerName,
  setNewCustomerName,
  newProductName,
  setNewProductName,
  newProductWeight,
  setNewProductWeight,
  newProductUnits,
  setNewProductUnits,
  newCoBrandingName,
  setNewCoBrandingName,
  creatingCustomer,
  creatingProduct,
  creatingCoBranding,
  // Handlers
  handleCreateCustomer,
  handleCreateProduct,
  handleCreateCoBranding,
  handleCreate,
  saving
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-lg"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Plan New Order</DialogTitle>
          <DialogDescription>
            Create a planned order with customer, product, and delivery details
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          {/* Customer Selection with inline create */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Customer *
            </Label>
            {!showCreateCustomer ? (
              <div className="flex gap-2">
                <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                  <SelectTrigger data-testid="select-planner-customer" className="flex-1">
                    <SelectValue placeholder="Select customer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={() => setShowCreateCustomer(true)} title="Create new customer">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  value={newCustomerName}
                  onChange={(e) => setNewCustomerName(e.target.value)}
                  placeholder="New customer name..."
                  className="flex-1"
                />
                <Button type="button" onClick={handleCreateCustomer} disabled={creatingCustomer} size="sm">
                  {creatingCustomer ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => { setShowCreateCustomer(false); setNewCustomerName(''); }}>
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {/* Product Selection with inline create */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Product
            </Label>
            {!showCreateProduct ? (
              <div className="flex gap-2">
                <Select value={formData.product_id} onValueChange={(v) => setFormData({ ...formData, product_id: v })}>
                  <SelectTrigger data-testid="select-planner-product" className="flex-1">
                    <SelectValue placeholder="Select product (optional)..." />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={() => setShowCreateProduct(true)} title="Create new product">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                <Input
                  value={newProductName}
                  onChange={(e) => setNewProductName(e.target.value)}
                  placeholder="Product name..."
                />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Weight/unit (kg)</Label>
                    <Input
                      type="number"
                      value={newProductWeight}
                      onChange={(e) => setNewProductWeight(e.target.value)}
                      placeholder="1.0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Units/case</Label>
                    <Input
                      type="number"
                      value={newProductUnits}
                      onChange={(e) => setNewProductUnits(e.target.value)}
                      placeholder="1"
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setShowCreateProduct(false); setNewProductName(''); }}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={handleCreateProduct} disabled={creatingProduct} size="sm">
                    {creatingProduct ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Product'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Co-Branding Selection with inline create */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Tags className="w-4 h-4" />
              Co-Branding (optional)
            </Label>
            {!showCreateCoBranding ? (
              <div className="flex gap-2">
                <Select value={formData.cobranding_id} onValueChange={(v) => setFormData({ ...formData, cobranding_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="select-planner-cobranding" className="flex-1">
                    <SelectValue placeholder="Select co-branding partner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {cobrandings.map(cb => (
                      <SelectItem key={cb.id} value={cb.id}>{cb.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={() => setShowCreateCoBranding(true)} title="Create new co-branding">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  value={newCoBrandingName}
                  onChange={(e) => setNewCoBrandingName(e.target.value)}
                  placeholder="New co-branding partner name..."
                  className="flex-1"
                />
                <Button type="button" onClick={handleCreateCoBranding} disabled={creatingCoBranding} size="sm">
                  {creatingCoBranding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => { setShowCreateCoBranding(false); setNewCoBrandingName(''); }}>
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {/* Quantity and Estimated Weight */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Units Count</Label>
              <Input
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                placeholder="e.g., 10"
                className="font-mono"
                data-testid="input-planner-quantity"
              />
            </div>
            <div className="space-y-2">
              <Label>Estimated Weight (kg)</Label>
              <Input
                type="number"
                value={formData.estimated_weight}
                onChange={(e) => setFormData({ ...formData, estimated_weight: e.target.value })}
                placeholder="e.g., 25.5"
                className="font-mono"
                data-testid="input-planner-weight"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>PO Number</Label>
            <Input
              value={formData.po_number}
              onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
              placeholder="e.g., 170197"
              className="font-mono"
            />
          </div>

          <div className="space-y-2">
            <Label>Production Date *</Label>
            <Popover modal={true}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start text-left font-mono"
                  data-testid="btn-select-date"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.dispatch_date || 'Select date...'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-[9999]" align="start" sideOffset={4}>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">When this product will be produced</p>
          </div>

          <div className="space-y-2">
            <Label>Transport Agent</Label>
            <Input
              value={formData.transport_agent}
              onChange={(e) => setFormData({ ...formData, transport_agent: e.target.value })}
              placeholder="e.g., Dawn, CMT, DPD"
            />
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Special instructions..."
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} data-testid="btn-create-planned-order">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Submit Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
